import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { 
  Upload, 
  FileSpreadsheet, 
  Users, 
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = process.env.REACT_APP_BACKEND_URL;

export default function BulkImport() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const isDark = theme === "dark";
  
  const [activeTab, setActiveTab] = useState("customers");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, filename: "" });
  
  const API = `${BASE_API}/api/b/${businessSlug}`;

  const ACCEPTED = ".csv,.pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.webp";

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setFiles(selected);
    setResults(null);
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) {
      toast.error("Please select at least one file first");
      return;
    }

    setUploading(true);
    setResults(null);

    const aggregated = { total_rows: 0, success_count: 0, error_count: 0, errors: [] };

    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress({ current: i + 1, total: files.length, filename: f.name });
        toast.loading(
          `Processing ${i + 1} of ${files.length}: ${f.name}`,
          { id: "bulk-import-progress" }
        );

        const formData = new FormData();
        formData.append("file", f);
        formData.append("type", activeTab);

        const isCSV = f.name.toLowerCase().endsWith(".csv");
        const endpoint = isCSV ? `${API}/import/csv` : `${API}/import/smart`;

        try {
          const response = await axios.post(endpoint, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 180000,
          });
          const r = response.data || {};
          aggregated.total_rows += r.total_rows || 0;
          aggregated.success_count += r.success_count || 0;
          aggregated.error_count += r.error_count || 0;
          if (Array.isArray(r.errors)) {
            for (const err of r.errors) {
              aggregated.errors.push({ ...err, file: f.name });
            }
          }
        } catch (error) {
          aggregated.error_count += 1;
          aggregated.errors.push({
            file: f.name,
            row: "-",
            message: error.response?.data?.detail || "Failed to process file",
          });
        }
      }

      toast.dismiss("bulk-import-progress");
      setResults(aggregated);

      if (aggregated.success_count > 0) {
        toast.success(`Imported ${aggregated.success_count} ${activeTab}`);
      }
      if (aggregated.error_count > 0) {
        toast.warning(`${aggregated.error_count} row(s) had errors`);
      }
      if (aggregated.success_count === 0 && aggregated.error_count === 0) {
        toast.error("No rows were imported");
      }
    } finally {
      setUploading(false);
      setProgress({ current: 0, total: 0, filename: "" });
    }
  };

  const downloadTemplate = (type) => {
    let csv = "";
    let filename = "";

    if (type === "customers") {
      csv = "name,email,phone,company_name,address,city,state,zip,tax_exempt,notes\n";
      csv += "John Doe,john@example.com,555-123-4567,ABC Company,123 Main St,Dayton,OH,45414,false,Regular customer\n";
      csv += "Jane Smith,jane@example.com,555-987-6543,XYZ Corp,456 Oak Ave,Columbus,OH,43215,true,Tax exempt trucking\n";
      filename = "customers_template.csv";
    } else {
      csv = "part_number,name,quantity,cost,price,category,description\n";
      csv += "BP-001,Brake Pads,50,25.00,45.00,Brakes,Front brake pads for trucks\n";
      csv += "OF-002,Oil Filter,100,8.00,15.00,Filters,Universal oil filter\n";
      filename = "inventory_template.csv";
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${filename}`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bulk-import-page">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Bulk Import
        </h1>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Import customers or inventory from CSV, Excel, Word, PDF, or image files. AI extracts the data automatically.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setFiles([]); setResults(null); }}>
        <TabsList className={`grid w-full max-w-md grid-cols-2 ${isDark ? 'bg-slate-800' : ''}`}>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Inventory
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="space-y-4 mt-4">
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <Users className="w-5 h-5 text-blue-500" />
                Import Customers
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : ''}>
                Upload customer data from CSV, Excel, Word, PDF, or image files
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Recommended columns (any document format works):
                </h4>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">name *</Badge>
                  <Badge variant="outline">email</Badge>
                  <Badge variant="outline">phone</Badge>
                  <Badge variant="outline">company_name</Badge>
                  <Badge variant="outline">address</Badge>
                  <Badge variant="outline">city</Badge>
                  <Badge variant="outline">state</Badge>
                  <Badge variant="outline">zip</Badge>
                  <Badge variant="outline">tax_exempt</Badge>
                  <Badge variant="outline">notes</Badge>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  * Required. CSV is processed instantly; Excel/Word/PDF/images use AI extraction. Duplicate emails are skipped.
                </p>
              </div>

              <Button 
                variant="outline" 
                onClick={() => downloadTemplate("customers")}
                className={isDark ? 'border-slate-600 hover:bg-slate-700' : ''}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory" className="space-y-4 mt-4">
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <Package className="w-5 h-5 text-green-500" />
                Import Inventory
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : ''}>
                Upload inventory items from CSV, Excel, Word, PDF, or image files
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Recommended columns (any document format works):
                </h4>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">name *</Badge>
                  <Badge variant="outline">part_number</Badge>
                  <Badge variant="outline">quantity *</Badge>
                  <Badge variant="outline">cost *</Badge>
                  <Badge variant="outline">price *</Badge>
                  <Badge variant="outline">category</Badge>
                  <Badge variant="outline">description</Badge>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  * Required. CSV is processed instantly; Excel/Word/PDF/images use AI extraction.
                </p>
              </div>

              <Button 
                variant="outline" 
                onClick={() => downloadTemplate("inventory")}
                className={isDark ? 'border-slate-600 hover:bg-slate-700' : ''}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Area */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
        <CardContent className="p-6">
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDark 
                ? 'border-slate-600 hover:border-blue-500' 
                : 'border-slate-300 hover:border-blue-400'
            } ${files.length > 0 ? 'border-green-500 bg-green-500/10' : ''}`}
          >
            <input
              type="file"
              accept={ACCEPTED}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
              data-testid="bulk-import-file-input"
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              {files.length > 0 ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-green-500" />
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {files.length === 1 ? files[0].name : `${files.length} files selected`}
                  </p>
                  {files.length > 1 && (
                    <ul className={`text-xs max-h-24 overflow-auto ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {files.map((f, i) => (
                        <li key={i}>{f.name} — {(f.size / 1024).toFixed(1)} KB</li>
                      ))}
                    </ul>
                  )}
                  {files.length === 1 && (
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {(files[0].size / 1024).toFixed(1)} KB
                    </p>
                  )}
                  <p className="text-xs text-green-500">Click to change files</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className={`w-12 h-12 mx-auto ${isDark ? 'text-slate-400' : 'text-slate-400'}`} />
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    Click to upload files
                  </p>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    CSV, Excel, Word, PDF, or images — multiple files supported
                  </p>
                </div>
              )}
            </label>
          </div>

          {files.length > 0 && (
            <div className="mt-4 flex justify-center">
              <Button 
                onClick={handleUpload}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                data-testid="bulk-import-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {progress.total > 1
                      ? `Processing ${progress.current}/${progress.total}...`
                      : 'Importing...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {activeTab === "customers" ? "Customers" : "Inventory"}
                    {files.length > 1 ? ` (${files.length} files)` : ''}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Rows</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {results.total_rows}
                </p>
              </div>
              <div className={`p-4 rounded-lg text-center bg-green-500/10`}>
                <p className="text-sm text-green-600">Successful</p>
                <p className="text-2xl font-bold text-green-600">
                  {results.success_count}
                </p>
              </div>
              <div className={`p-4 rounded-lg text-center ${results.error_count > 0 ? 'bg-red-500/10' : isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
                <p className={`text-sm ${results.error_count > 0 ? 'text-red-600' : isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Errors
                </p>
                <p className={`text-2xl font-bold ${results.error_count > 0 ? 'text-red-600' : isDark ? 'text-white' : 'text-slate-800'}`}>
                  {results.error_count}
                </p>
              </div>
            </div>

            {results.errors && results.errors.length > 0 && (
              <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                <h4 className="font-medium text-red-600 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Errors
                </h4>
                <ul className="space-y-1 text-sm">
                  {results.errors.map((error, idx) => (
                    <li key={idx} className={isDark ? 'text-red-300' : 'text-red-700'}>
                      {error.file ? `${error.file} — ` : ''}Row {error.row}: {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {results.success_count > 0 && (
              <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                <p className="text-green-600 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Successfully imported {results.success_count} {activeTab}!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
